import { ILocationSearchResult } from '@travelnest/merlin.location-search';
import { ICreatablePromotion, IPromotionOpportunity } from '../../../data/entities/promotions';
import { IListableContract } from '../../../entities/contracts/types';
import { IChannelPromotion } from '../promotions-list/PromotionsList';
interface ILocation {
	address1?: string;
	address2?: string;
	city?: string;
	postcode?: string;
	country_code: string;
	latitude: number;
	longitude: number;
}

// @ts-ignore
type AddressComponent = NonNullable<ILocationSearchResult['result']>[number]['address_components'][number];

const findValueInComponents = (
	components: AddressComponent[],
	placeTypes: string | string[],
	matchFunc: 'every' | 'some' = 'every',
	key: 'long_name' | 'short_name' = 'long_name',

): string | null => {
	const toFind = (typeof placeTypes === 'string') ? [placeTypes] : placeTypes;
	const foundVal = components.find((c) => {
		if (matchFunc === 'every') {
			return toFind.every((t) => c.types.includes(t));
		} else {
			return toFind.some((t) => c.types.includes(t));
		}
	});
	return foundVal ? foundVal[key] : null;
};

const getCity = (components: AddressComponent[]) => {
	const area = findValueInComponents(components, 'administrative_area_level_3');
	const city = findValueInComponents(components, 'postal_town');
	const locality = findValueInComponents(components, 'locality');

	return city || locality || area || null;
};

const getAddress2 = (components: AddressComponent[], city: string | null) => {
	const locality = findValueInComponents(components, 'locality');
	const neighbourhood = findValueInComponents(components, 'neighborhood');

	// If we already have a city && locality, set locality to address 2
	if (city && locality && city !== locality) {
		return locality;
	}

	if (!locality && neighbourhood) {
		return neighbourhood;
	}

	return null;
};

const getAddress1 = (components: AddressComponent[]) => {
	const route = findValueInComponents(components, 'route');
	const intersection = findValueInComponents(components, 'intersection');
	const streetNumber = findValueInComponents(components, 'street_number');

	let address1 = '';
	if (route) {
		address1 = route;
	}

	if (intersection) {
		address1 = intersection;
	}

	if (address1 && streetNumber) {
		address1 = `${streetNumber} ${address1}`;
		return address1;
	}

	if (address1) {
		return address1;
	}

	return null;
};

/**
 * Generate a Travelnest-friendly location from a set of Google Places `GeocoderAddressComponent`s.
 *
 * Throws an error if there isn't at _least_ a countryCode in the input components.
 *
 * @returns ILocationNoLatLng: The application's ILocation excluding `latitude` and `longitude`
 */
export const locationFromGoogleAddressComponents = (result: ILocationSearchResult): ILocation => {
	const first = result.result && result.result?.length > 0 ? result.result[0] : null;
	const components = first?.address_components || [];
	if (!first) {
		throw new Error(`Couldn\'t generate a location from empty geocoder result`);
	}

	const countryCode = findValueInComponents(components, ['country', 'political'], 'every', 'short_name');
	if (!countryCode) {
		throw new Error(`Couldn\'t generate a location from: ${JSON.stringify(components)}`);
	}

	const city = getCity(components);
	const postcode = findValueInComponents(components, ['postal_code', 'postal_code_prefix'], 'some');
	const address2 = getAddress2(components, city);
	const address1 = getAddress1(components);
	const lat = first.geometry.location.lat();
	const long = first.geometry.location.lng();

	return {
		country_code: countryCode,
		postcode: postcode || undefined,
		city: city || undefined,
		address1: address1 || undefined,
		address2: address2 || undefined,
		latitude: lat,
		longitude: long
	};
};

export const formatPromotionForSend = (propertyId: number, promotionOpportunity: IPromotionOpportunity): ICreatablePromotion => {
	return {
		channel_id: promotionOpportunity.channel_id,
		property_id: propertyId,
		internal_reference: promotionOpportunity.internal_reference,
		rate: promotionOpportunity.default_rate,
		amount: promotionOpportunity.default_amount,
	} as ICreatablePromotion;
};

const channelNames: { [key: string]: { name: string; imageURL: string } } = {
	'airbnb.com': {
		name: 'Airbnb',
		imageURL: '/71eb904/assets/images/channels/airbnb_small.svg',
	},
};
const formatChannelName = (id: string) => channelNames[id].name;
const imageURLForChannel = (id: string) => channelNames[id].imageURL;

export const buildChannelPromotions = (promotions: IPromotionOpportunity[] = [], channelContracts: IListableContract[] = []) => {
	const availableForSignup = promotions
		.filter((p) => p.is_accepting_signup)
		.filter((p) => channelContracts.filter((c) => c.channel_id === p.channel_id && c.list_property).length > 0)
		.filter((p) => p.channel_id === 'airbnb.com' && p.internal_reference === 'AIRBNB_NEW_LISTING')
		// Sort promotions by most recently updated in lieu of better sort ordering
		.sort((a, b) => a.updated_at < b.updated_at ? 1 : -1);

	const filteredChannels = availableForSignup.reduce((channels, promo) => {
		const channelArr = channels.filter(({ id }) => id === promo.channel_id);
		if (channelArr.length === 0) {
			const channelPromo: IChannelPromotion = {
				id: promo.channel_id,
				name: formatChannelName(promo.channel_id),
				imageURL: imageURLForChannel(promo.channel_id),
				promotions: [promo],
			};
			channels.push(channelPromo);
			return channels;
		} else {
			const channel = channelArr[0];
			channel.promotions.push(promo);
			return channels;
		}
	}, [] as IChannelPromotion[]);

	return (filteredChannels);
};
